'use strict';

const { queryFirst, queryAll, hasClass, addClass, removeClass, debounce } = require('../domUtil');

const headerEl = queryFirst('.main-header');
const ROTATION_HIDE_MS = 300;
let rotationHideTimer;

/**
 * Returns the rendered height of the hero media container.
 * .hero-asset-picture (image) and .video-component (video) are the elements
 * that carry the aspect-ratio CSS and define the actual display size.
 * @param {Element} heroAsset
 * @returns {number}
 */
function getHeroAssetHeight(heroAsset) {
    const mediaEl = queryFirst('.video-component, .hero-asset-picture', heroAsset);
    return mediaEl ? mediaEl.getBoundingClientRect().height : heroAsset.getBoundingClientRect().height;
}

/**
 * Computes and applies logo dimensions and transform as inline styles
 * to all div.header-nav-logo elements inside the header.
 * @param {Element} heroAsset
 */
function placeLogo(heroAsset) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const isPortrait = vh > vw;
    const assetHeight = getHeroAssetHeight(heroAsset);
    const workingHeight = assetHeight >= vh ? vh : assetHeight;

    // Custom aspect ratio banners have unpredictable heights that conflict with logo
    // placement and text captions. Disable logo-animated unconditionally.
    if (hasClass(heroAsset, 'm-custom-aspect')) {
        removeClass(headerEl, 'logo-animated');
        return;
    }

    let logoHeight, logoWidth;

    // logo dymentions based on 110x57 svg
    if (isPortrait) {
        logoWidth = vw > 480 ? vw / 2 : 350;
        logoHeight = logoWidth * 0.52;
    } else {
        logoHeight = vh / 3;
        logoWidth = logoHeight * 1.47
    }

    const translateX = isPortrait ? vw / 2 - logoWidth / 2 : 0; // center horizontally on portrait only

    let translateY = 0;
    const isTop    = (isPortrait && hasClass(headerEl, 'logo-mobile-top'))    || (!isPortrait && hasClass(headerEl, 'logo-desktop-top'));
    const isMiddle = (isPortrait && hasClass(headerEl, 'logo-mobile-middle')) || (!isPortrait && hasClass(headerEl, 'logo-desktop-middle'));
    const isBottom = (isPortrait && hasClass(headerEl, 'logo-mobile-bottom')) || (!isPortrait && hasClass(headerEl, 'logo-desktop-bottom'));

    if (isTop) {
        const topNav = queryFirst('.page-top-navigation');
        const topNavHeight = topNav ? topNav.getBoundingClientRect().height : 0;

        translateY = topNavHeight + 30;
    } else if (isMiddle) {
        translateY = workingHeight / 2 - logoHeight / 2;
    } else if (isBottom) {
        translateY = workingHeight - logoHeight - 30;
    }

    queryAll('div.header-nav-logo', headerEl).forEach(logoEl => {
        logoEl.style.height = `${logoHeight}px`;
        logoEl.style.width = `${logoWidth}px`;
        logoEl.style.transform = `translateX(${translateX}px) translateY(${translateY}px)`;
        logoEl.style.opacity = '1';
    });
}

/**
 * Resolves when the hero media container has a measurable height.
 * For images: the .hero-asset-picture box height is driven by the img load,
 * so we wait for the img load event before measuring.
 * For videos: .video-component height is set by CSS aspect-ratio, so it is
 * available immediately without waiting for video data.
 * @param {Element} heroAsset
 * @returns {Promise<void>}
 */
function waitForHeroMedia(heroAsset) {
    return new Promise(resolve => {
        const img = queryFirst('.hero-asset-picture img', heroAsset);

        if (img) {
            if (img.complete && img.naturalHeight > 0) {
                resolve();
            } else {
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true });
            }
            return;
        }

        resolve();
    });
}

/**
 * Returns the .hero-asset in homepage slot 1, whether it is the direct first
 * child of .homepage-items or nested inside a slot wrapper element.
 * @param {Element} homepageItems
 * @returns {Element|null}
 */
function getFirstSlotHeroAsset(homepageItems) {
    const firstSlot = homepageItems.firstElementChild;
    if (!firstSlot) return null;
    if (hasClass(firstSlot, 'hero-asset')) return firstSlot;
    return queryFirst('.hero-asset', firstSlot);
}

/**
 * Handles the scheduling edge case where a hero with .m-caption-top appears as
 * the first slot while the header is set to logo-animated + header-transparent.
 * The caption occupies the top of the banner, conflicting with the transparent
 * header — so we strip header-transparent from the top navigation and bail out
 * of logo placement entirely.
 * @param {Element} homepageItems
 * @returns {boolean} true if the conflict was detected and handled
 */
function handleCaptionTopConflict(homepageItems) {
    const firstSlot = homepageItems.firstElementChild;
    if (!firstSlot || !hasClass(firstSlot, 'm-caption-top')) return false;

    const topNav = queryFirst('.page-top-navigation');
    if (!topNav || !hasClass(topNav, 'header-transparent')) {
        return true;
    }

    if (hasClass(topNav, 'animate-header')) {
        return true;
    }

    addClass(topNav, 'animate-header');

    const mainHeader = queryFirst('.main-header', topNav);
    mainHeader?.addEventListener('transitionend', event => {
        if (event.target !== mainHeader || event.propertyName !== 'margin-bottom') {
            return;
        }

        removeClass(topNav, 'header-transparent', 'animate-header');
        removeClass(headerEl, 'logo-animated');
    }, { once: true });

    return true;
}

/**
 * Hides first-slot hero media during orientation change via body.is-rotating;
 * CSS fades media back in when the class is removed after a short delay.
 * @param {Element} homepageItems
 */
function handleOrientationChange(homepageItems) {
    if (!getFirstSlotHeroAsset(homepageItems)) return;

    addClass(document.body, 'is-rotating');
    clearTimeout(rotationHideTimer);
    rotationHideTimer = setTimeout(() => removeClass(document.body, 'is-rotating'), ROTATION_HIDE_MS);
}

/**
 * Registers listeners for orientation changes. matchMedia covers desktop
 * device-toolbar rotation where orientationchange may not fire.
 * @param {Element} homepageItems
 */
function bindOrientationChange(homepageItems) {
    const onRotate = () => handleOrientationChange(homepageItems);

    window.addEventListener('orientationchange', onRotate);

    const orientationQuery = window.matchMedia('(orientation: portrait)');
    if (orientationQuery.addEventListener) {
        orientationQuery.addEventListener('change', onRotate);
    } else if (orientationQuery.addListener) {
        orientationQuery.addListener(onRotate);
    }
}

/**
 * Re-computes placement using already-loaded hero asset (used on resize).
 * requestAnimationFrame defers measurement to after the browser has committed
 * the new layout, which matters on orientation change where getBoundingClientRect
 * can still return stale dimensions when the resize event first fires.
 */
function defineLogoPlacement() {
    if (!hasClass(headerEl, 'logo-animated')) return;

    const homepageItems = queryFirst('.homepage-items');
    if (!homepageItems) return;

    if (handleCaptionTopConflict(homepageItems)) return;

    const heroAsset = getFirstSlotHeroAsset(homepageItems);
    if (!heroAsset) return;

    requestAnimationFrame(() => placeLogo(heroAsset));
}

/**
 * Watches .homepage-items for the first slot to receive a .hero-asset,
 * then calls callback with that element. Resolves immediately if already present.
 * @param {Element} homepageItems
 * @param {Function} callback
 */
function observeFirstSlotHeroAsset(homepageItems, callback) {
    const heroAsset = getFirstSlotHeroAsset(homepageItems);
    if (heroAsset) {
        callback(heroAsset);
        return;
    }

    const observer = new MutationObserver(() => {
        const currentHero = getFirstSlotHeroAsset(homepageItems);
        if (!currentHero) return;

        observer.disconnect();
        callback(currentHero);
    });

    observer.observe(homepageItems, { childList: true, subtree: true });
}

module.exports = () => {
    if (!queryFirst('[data-action="Home-Show"]')) return;

    const homepageItems = queryFirst('.homepage-items');
    if (!homepageItems) return;

    bindOrientationChange(homepageItems);

    if (!hasClass(headerEl, 'logo-animated')) return;

    observeFirstSlotHeroAsset(homepageItems, heroAsset => {
        if (handleCaptionTopConflict(homepageItems)) return;
        waitForHeroMedia(heroAsset).then(() => placeLogo(heroAsset));
    });

    window.addEventListener('resize', debounce(defineLogoPlacement, 100));
};
